import React, { useCallback, useEffect, useState } from "react";
import { Award, Eye, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TEH_LABEL } from "./SdoDashboard";

const lb = (a) => (a.data?.village || a.data?.city || "—");
const applicant = (a) => a.data?.nameMr || a.applicantName || "—";

export default function ResultTab({ canApprove = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // {tehsil, lb}
  const [busy, setBusy] = useState(false);
  const [tehFilter, setTehFilter] = useState("");
  const [lbFilter, setLbFilter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.resultList().then((d) => setRows(d || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // group by tehsil + lb
  const groups = {};
  rows.forEach((a) => {
    const name = lb(a);
    if (!name || name === "—") return; // skip apps with no local body
    const w = a.data?.lbWard || "";
    const key = `${a.tehsil}||${name}||${w}`;
    groups[key] = groups[key] || { tehsil: a.tehsil, lb: name, ward: w, apps: [] };
    groups[key].apps.push(a);
  });
  const bodies = Object.values(groups).sort((a, b) => (a.tehsil || "").localeCompare(b.tehsil || ""));

  const approve = async (appId, name) => {
    if (!window.confirm(`या स्थानिक स्वराज्य संस्थेचा निकाल घोषित करायचा? रिक्त पदांच्या संख्येनुसार सर्वाधिक गुण असलेले वरचे उमेदवार "मंजूर" व उर्वरित "नामंजूर" होतील. ही कृती परत करता येणार नाही.`)) return;
    setBusy(true);
    try {
      await api.resultApprove(appId);
      toast.success("निकाल घोषित झाला — मंजूर.");
      load();
    } catch (e) { toast.error("त्रुटी: " + (e.message || "")); }
    setBusy(false);
  };

  const sendAllLetters = async () => {
    if (!window.confirm("सर्व मंजूर अर्जदारांना मंजुरी पत्र (PDF) ईमेल करायचे? हे फक्त 'मंजूर' अर्जदारांनाच पाठवले जाईल.")) return;
    setBusy(true);
    try {
      const r = await api.sendAllApprovalLetters();
      toast.success(`मंजुरी पत्र पाठवले — ${r?.sent || 0} मंजूर अर्जदारांना${r?.skipped ? ` (${r.skipped} वगळले)` : ""}.`);
      load();
    } catch (e) { toast.error("त्रुटी: " + (e.message || "")); }
    setBusy(false);
  };

  const taluka = (a) => a.data?.taluka || a.data?.talukaRO || TEH_LABEL[a.tehsil] || a.tehsil;

  const writeXls = (head, rowsX, filename) => {
    const html = `<table border="1"><tr>${head.map((h) => `<th>${h}</th>`).join("")}</tr>${rowsX.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}</table>`;
    const blob = new Blob(["\ufeff<html><head><meta charset='UTF-8'></head><body>" + html + "</body></html>"], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportResultExcel = (apps, bodyName, tehsil) => {
    const head = ["अ.क्र.", "तालुका", "स्थानिक स्वराज्य संस्था नाव", "अर्ज क्र.", "अर्जदाराचे नाव", "मिळालेले एकूण गुण", "निकाल"];
    const rowsX = apps.map((a, i) => [i + 1, taluka(a), lb(a), a.applicationNumber, applicant(a), a.markingDone ? `${a.markingTotal}/100` : "—", a.result || "—"]);
    writeXls(head, rowsX, `निकाल_${bodyName}.xls`);
  };

  // district / tehsil-wise list of मंजूर applications
  const exportApprovedExcel = () => {
    const approved = rows
      .filter((a) => a.result === "मंजूर" && a.resultDeclared && (!tehFilter || a.tehsil === tehFilter) && (!lbFilter || lb(a) === lbFilter))
      .sort((a, b) => taluka(a).localeCompare(taluka(b)) || (b.markingTotal || 0) - (a.markingTotal || 0));
    if (approved.length === 0) { toast.error("मंजूर अर्ज उपलब्ध नाहीत."); return; }
    const head = ["अ.क्र.", "तालुका", "स्थानिक स्वराज्य संस्था नाव", "अर्ज क्र.", "अर्जदाराचे नाव", "मिळालेले एकूण गुण", "निकाल"];
    const rowsX = approved.map((a, i) => [i + 1, taluka(a), lb(a), a.applicationNumber, applicant(a), a.markingDone ? `${a.markingTotal}/100` : "—", a.result]);
    const scope = tehFilter ? (TEH_LABEL[tehFilter] || tehFilter) : "संपूर्ण_जिल्हा";
    writeXls(head, rowsX, `मंजूर_अर्ज_${scope}.xls`);
    api.auditExport({ kind: "result_approved_list", scope, count: approved.length });
  };

  if (open) {
    const g = groups[`${open.tehsil}||${open.lb}||${open.ward || ""}`];
    const apps = (g?.apps || []).slice().sort((a, b) => (b.markingTotal || 0) - (a.markingTotal || 0));
    const declared = apps.some((a) => a.resultDeclared);
    const vac = apps.length ? (apps[0].vacancies || 1) : 1;
    const topIds = apps.slice(0, vac).map((a) => a.id);
    const topId = apps.length ? apps[0].id : null;
    const doneCount = apps.filter((a) => a.markingComplete).length;
    const allMarked = apps.length > 0 && doneCount === apps.length;
    const ward = apps.length ? (apps[0].data?.lbWard || "") : "";
    return (
      <div>
        <button onClick={() => setOpen(null)} className="mb-3 rounded-full border border-slate-300 px-4 py-1.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50" data-testid="result-back">← यादीकडे परत</button>
        <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[15px] font-bold text-[#1b5e3f]">{lb(g.apps[0])}{ward ? ` — ${ward}` : ""} — गुणवत्ता यादी व निकाल ({TEH_LABEL[open.tehsil] || open.tehsil}) · रिक्त पदे: {vac}</p>
            {declared && (
              <button onClick={() => exportResultExcel(apps, lb(g.apps[0]), open.tehsil)} data-testid="result-excel"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-4 py-2 text-[12.5px] font-semibold text-white hover:bg-[#144d33]">
                निकाल Excel डाउनलोड
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="result-applicant-table">
              <thead>
                <tr className="bg-[#1b5e3f] text-left text-white">
                  <th className="p-2.5 font-semibold">अ.क्र.</th>
                  <th className="p-2.5 font-semibold">स्थानिक स्वराज्य संस्था नाव</th>
                  {ward && <th className="p-2.5 font-semibold">वॉर्ड / प्रभाग</th>}
                  <th className="p-2.5 font-semibold">अर्ज क्र.</th>
                  <th className="p-2.5 font-semibold">अर्जदाराचे नाव</th>
                  <th className="p-2.5 text-center font-semibold">मिळालेले एकूण गुण</th>
                  <th className="p-2.5 text-center font-semibold">निकाल</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a, i) => {
                  const isTop = topIds.includes(a.id);
                  return (
                    <tr key={a.id} className={`border-b border-slate-100 ${isTop && !declared ? "bg-emerald-50" : ""}`}>
                      <td className="p-2.5">{i + 1}</td>
                      <td className="p-2.5">{lb(a)}</td>
                      {ward && <td className="p-2.5">{a.data?.lbWard || "—"}</td>}
                      <td className="p-2.5 font-latin font-semibold text-[#1b3d2a]">{a.applicationNumber}</td>
                      <td className="p-2.5">{applicant(a)} {isTop && <span className="ml-1 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold text-white">गुणवत्ता क्र. {i + 1}</span>}</td>
                      <td className="p-2.5 text-center font-bold text-[#1b5e3f]">{a.markingComplete ? `${a.markingTotal}/100` : "—"}</td>
                      <td className="p-2.5 text-center">
                        {a.resultDeclared ? (
                          <span className={`rounded-full px-3 py-1 text-[12px] font-bold ${a.result === "मंजूर" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>{a.result}</span>
                        ) : canApprove ? (
                          a.id === topId ? (
                            allMarked ? (
                              <button onClick={() => approve(a.id, applicant(a))} disabled={busy} data-testid={`result-approve-${a.id}`}
                                className="rounded-full bg-emerald-600 px-4 py-1.5 text-[12.5px] font-bold text-white ring-2 ring-emerald-300 hover:bg-emerald-700 disabled:opacity-50">निकाल घोषित करा</button>
                            ) : (
                              <button disabled title="सर्व अर्जदारांचे गुणांकन पूर्ण झालेले नाही" data-testid={`result-approve-${a.id}`}
                                className="cursor-not-allowed rounded-full bg-slate-200 px-4 py-1.5 text-[12.5px] font-bold text-slate-400">निकाल घोषित करा</button>
                            )
                          ) : (
                            <span className="text-[12px] text-slate-400">—</span>
                          )
                        ) : (
                          <span className="text-[12px] text-slate-400">प्रलंबित</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {canApprove && !declared && !allMarked && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] font-semibold text-amber-800" data-testid="result-marking-incomplete">
              या संस्थेतील सर्व अर्जदारांचे गुणांकन पूर्ण झाल्यावरच निकाल घोषित करता येईल ({apps.length} पैकी {doneCount} पूर्ण).
            </p>
          )}
          {canApprove && !declared && allMarked && <p className="mt-3 text-[12px] text-slate-500">या स्थानिक स्वराज्य संस्थेसाठी <b>{vac}</b> रिक्त पद(े) आहेत. "निकाल घोषित करा" केल्यास सर्वाधिक गुण असलेले वरचे <b>{vac}</b> उमेदवार आपोआप "मंजूर" व उर्वरित सर्व "नामंजूर" होतील.</p>}
        </div>
      </div>
    );
  }

  // per-tehsil aggregate (one row per tehsil)
  const tehMap = {};
  bodies.forEach((b) => {
    tehMap[b.tehsil] = tehMap[b.tehsil] || { tehsil: b.tehsil, count: 0, dec: 0 };
    tehMap[b.tehsil].count += 1;
    if (b.apps.every((a) => a.resultDeclared)) tehMap[b.tehsil].dec += 1;
  });
  const tehRows = Object.values(tehMap).sort((a, b) => (a.tehsil || "").localeCompare(b.tehsil || ""));
  const tehOpts = [...new Set(bodies.map((b) => b.tehsil))];
  const lbOpts = [...new Set(bodies.filter((b) => !tehFilter || b.tehsil === tehFilter).map((b) => b.lb))];
  const bodyDeclared = (b) => b.apps.length > 0 && b.apps.every((a) => a.resultDeclared);
  const fBodies = bodies
    .filter((b) => (!tehFilter || b.tehsil === tehFilter) && (!lbFilter || b.lb === lbFilter))
    .map((b) => ({ ...b, _declared: bodyDeclared(b) }))
    .sort((a, b) => (a._declared === b._declared ? 0 : a._declared ? 1 : -1)); // pending first, declared to bottom
  const approvedApps = rows.filter((a) => a.resultDeclared && a.result === "मंजूर");
  const pendingLetters = approvedApps.filter((a) => !a.approvalLetterSent).length;

  return (
    <div>
      {canApprove && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-[#e6dcc4] bg-[#fbf9f2] px-4 py-3">
          <p className="text-[13px] text-slate-600">
            मंजूर अर्जदार: <b className="text-[#1b5e3f]">{approvedApps.length}</b>
            {approvedApps.length > 0 && <> · मंजुरी पत्र प्रलंबित: <b className="text-amber-700">{pendingLetters}</b></>}
          </p>
          <button onClick={sendAllLetters} disabled={busy || approvedApps.length === 0} data-testid="send-all-letters"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#c2570a] px-5 py-2 text-[13px] font-bold text-white hover:opacity-90 disabled:opacity-50">
            सर्व मंजूर अर्जदारांना मंजुरी पत्र ईमेल करा
          </button>
        </div>
      )}
      {/* chart — one row per tehsil */}
      <div className="mb-5 overflow-x-auto rounded-2xl border border-[#e6dcc4] bg-white p-4">
        <p className="mb-2 text-[14px] font-bold text-[#1b5e3f]">तहसील कार्यालयनिहाय निकाल प्रगती</p>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-100 text-slate-600">
              <th className="p-2 text-left" rowSpan={2}>अ.क्र.</th>
              <th className="p-2 text-left" rowSpan={2}>तहसील कार्यालय</th>
              <th className="p-2 text-center" colSpan={3}>स्थानिक स्वराज्य संस्था</th>
            </tr>
            <tr className="bg-slate-100 text-slate-600">
              <th className="p-2 text-center">एकूण संख्या</th>
              <th className="p-2 text-center">निकाल घोषित</th>
              <th className="p-2 text-center">निकाल प्रलंबित</th>
            </tr>
          </thead>
          <tbody>
            {tehRows.map((t, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="p-2">{i + 1}</td>
                <td className="p-2 font-semibold text-[#1b3d2a]">{TEH_LABEL[t.tehsil] || t.tehsil}</td>
                <td className="p-2 text-center">{t.count}</td>
                <td className="p-2 text-center text-emerald-700">{t.dec}</td>
                <td className="p-2 text-center text-amber-700">{t.count - t.dec}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* list */}
      <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <p className="flex items-center gap-2 text-[15px] font-bold text-[#1b5e3f]"><Award className="h-4 w-4" /> स्थानिक स्वराज्य संस्थानिहाय निकाल</p>
          <select value={tehFilter} onChange={(e) => { setTehFilter(e.target.value); setLbFilter(""); }} data-testid="result-filter-tehsil"
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-[13px] outline-none focus:border-[#1b5e3f]">
            <option value="">सर्व तहसील</option>
            {tehOpts.map((t) => <option key={t} value={t}>{TEH_LABEL[t] || t}</option>)}
          </select>
          <select value={lbFilter} onChange={(e) => setLbFilter(e.target.value)} data-testid="result-filter-lb"
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-[13px] outline-none focus:border-[#1b5e3f]">
            <option value="">सर्व संस्था</option>
            {lbOpts.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <button onClick={exportApprovedExcel} data-testid="result-approved-excel"
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#144d33]">
            <FileDown className="h-3.5 w-3.5" /> निकाल Excel डाउनलोड (मंजूर अर्ज)
          </button>
        </div>
        {loading ? <div className="py-8 text-center text-slate-400">लोड होत आहे...</div> :
          fBodies.length === 0 ? <div className="py-8 text-center text-slate-400" data-testid="result-empty">निकालासाठी पात्र अर्ज नाहीत.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="result-body-table">
              <thead>
                <tr className="bg-[#1b5e3f] text-left text-white">
                  <th className="p-2.5 font-semibold">अ.क्र.</th>
                  <th className="p-2.5 font-semibold">तहसील कार्यालय</th>
                  <th className="p-2.5 font-semibold">स्थानिक स्वराज्य संस्था नाव</th>
                  <th className="p-2.5 text-center font-semibold">निकाल पहा</th>
                </tr>
              </thead>
              <tbody>
                {fBodies.map((b, i) => (
                  <tr key={i} data-testid={`result-body-row-${i}`}
                    className={`border-b border-slate-100 transition-colors ${b._declared ? "bg-[#e7f6ea] hover:bg-[#daf0df]" : "bg-[#fdf7e3] hover:bg-[#f9efcf]"}`}>
                    <td className="p-2.5">{i + 1}</td>
                    <td className="p-2.5">{TEH_LABEL[b.tehsil] || b.tehsil}</td>
                    <td className="p-2.5 font-semibold text-[#1b3d2a]">
                      {b.lb}{b.ward ? <span className="ml-1.5 text-[12px] font-normal text-slate-500">({b.ward})</span> : null}
                      <span className={`ml-2 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${b._declared ? "bg-emerald-200 text-emerald-800" : "bg-amber-200 text-amber-800"}`}>
                        {b._declared ? "निकाल घोषित" : "निकाल प्रलंबित"}
                      </span>
                    </td>
                    <td className="p-2.5 text-center">
                      <button onClick={() => setOpen({ tehsil: b.tehsil, lb: b.lb, ward: b.ward })} data-testid={`result-view-${i}`}
                        className="inline-flex items-center gap-1 rounded-full bg-[#c2570a] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:opacity-90"><Eye className="h-3.5 w-3.5" /> निकाल पहा</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
